import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AUDIT_PAGE_DEFAULT, type AuditListResponse } from "@steelbill/types";
import { api } from "@/lib/api";
import { endpoints } from "@/lib/endpoints";
import { queryKeys } from "@/lib/query-keys";

/**
 * Reading `audit_log` (spec §13, §14.3) — the one query behind `audit.view`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THERE IS NO `total` AND THEREFORE NO PAGE COUNT. `GET /audit` deliberately
 * does not compute one: a `COUNT(*)` over a filtered six-year table is an index
 * scan of every matching row, paid on EVERY page, and it is the single request
 * on this screen that could stall MariaDB on a small VPS that is also serving
 * the father's phone.
 *
 * So paging is `hasMore`, answered out of the `limit + 1` rows the query was
 * fetching anyway. Anything that would need a total — "page 3 of 40", a
 * progress bar, a jump-to-last control — cannot be built on this endpoint, and
 * estimating one would be the client inventing a number the server refused to.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * READING THE LOG WRITES NO ROW TO IT (services/audit-query.ts sets out why at
 * length), so this query is free to refetch. That is what makes
 * `keepPreviousData` the right choice below rather than a nicety: stepping
 * between pages keeps the last page painted instead of collapsing the screen to
 * a skeleton, and nothing is being logged for the privilege.
 */

/** Every filter the screen holds, as `AuditQuerySchema` spells them. */
export interface AuditFilters {
  /** Everything one person did. Indexed by `(actor_user_id, created_at)`. */
  actorUserId?: number | undefined;
  entityType?: string | undefined;
  action?: string | undefined;
  /** INCLUSIVE calendar days, `YYYY-MM-DD`, resolved by the server in IST. */
  from?: string | undefined;
  to?: string | undefined;
  offset?: number | undefined;
}

/**
 * A page of the log, newest first.
 *
 * ONLY THE KEYS THAT ARE SET ARE SENT. `AuditQuerySchema` is `.strict()`, and
 * while its `optionalQuery` preprocess rewrites `""` to `undefined` server-side,
 * an empty string still travels in the URL and lands in the cache key — so two
 * identical requests would occupy two cache entries. `lib/api.ts` drops
 * `undefined` values from the query string; this builds the object it drops from.
 *
 * `entityId` IS NOT OFFERED. The schema refuses it without an `entityType`
 * beside it (id 5 exists in every table in this database), and nothing on this
 * screen asks "one document's whole history" — that question belongs on the
 * document, not here.
 */
export function useAuditEntries(filters: AuditFilters = {}) {
  const query = {
    ...(filters.actorUserId === undefined ? {} : { actorUserId: filters.actorUserId }),
    ...(filters.entityType === undefined ? {} : { entityType: filters.entityType }),
    ...(filters.action === undefined ? {} : { action: filters.action }),
    ...(filters.from === undefined || filters.from === "" ? {} : { from: filters.from }),
    ...(filters.to === undefined || filters.to === "" ? {} : { to: filters.to }),
    limit: AUDIT_PAGE_DEFAULT,
    offset: filters.offset ?? 0,
  };

  return useQuery({
    queryKey: queryKeys.audit.list(query),
    queryFn: async ({ signal }) =>
      api.get<AuditListResponse>(endpoints.audit, { query, signal }),
    /*
     * The previous page stays on screen while the next one loads. On an
     * append-only log this is honest — the rows are immutable, so what is
     * showing is not stale, it is simply the page before.
     */
    placeholderData: keepPreviousData,
  });
}
